﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

import * as React from "react";
import { createUseStyles } from "react-jss";
import { useSearchParams } from "react-router-dom";
import { Icon } from "~base/Components/Primitives/Icon";
import { Ingress } from "~base/Components/Primitives/Ingress";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import Container from "~base/Components/Layout/Container";
import SplashPage from "~base/Pages/Shared/SplashPage";
import { useGetOperation } from "~base/Services/API/DataGathering/GetOperation";
import { GatheringStatus } from "~base/Services/API/DataGathering/Model";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import type { SoPConsilioTheme } from "~irad/Styling/SoPConsilioTheme";
import { initialState, reducer } from "./Store";
import classnames from "classnames";

//-------------------------------------------------------------------------------------
// Styling
//-------------------------------------------------------------------------------------

const useStyles = createUseStyles((theme: SoPConsilioTheme) => ({
    container: {
        width: "100vw",
        height: "100vh",
        backgroundColor: theme.constants.secondaryBorderColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    text: {
        color: "#66C5EC",
    },
    title: {
        marginTop: "0px",
        fontSize: "48px",
    },
    ingress: {
        fontSize: "16px",
    },
    sopLogo: {
        position: "absolute",
        top: "50px",
        left: "83px",
        width: "auto",
        height: "unset",
        marginBottom: "unset",
        opacity: "0.3",
    },
    iconSuccess: {
        width: "70px",
        height: "70px",
    },
    iconError: {
        width: "61px",
        height: "61px",
    },
}));

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const TinkPageComponent: React.FunctionComponent<unknown> = () => {
    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("TinkPage"),
        reducer,
        initialState()
    );

    // Init hooks
    const classes = useStyles();
    const [searchParams] = useSearchParams();

    const requestId = searchParams.get("requestId");

    // API hooks
    const callGetOperation =
        requestId !== null &&
        state?.operationInfo?.Status !== GatheringStatus.Error &&
        state?.operationInfo?.Status !== GatheringStatus.Success;

    const [loadingGetOperation, ______] = useGetOperation({
        operationId: requestId,
        call: callGetOperation,
        callback: async (status, _, responseData) => {
            dispatch({
                type: "setOperationInfo",
                data: responseData,
                status: status,
            });
        },
    });

    // Rendering
    const pageLoading = state.loading || loadingGetOperation;
    const itemStatus = (state.operationInfo?.Items ?? [])[0]?.Status;

    return (
        <SplashPage isLoading={pageLoading} showSplashScreen={false}>
            <Container className={classes.container}>
                <Icon
                    iconStyleType={"Step"}
                    iconFQN={"Consilio.UI.Icons.TinkPage.SoPLogo"}
                    svgClassName={classes.sopLogo}
                />
                <div style={{ width: "auto" }}>
                    {itemStatus === GatheringStatus.Success ? (
                        <React.Fragment>
                            <Icon
                                iconStyleType={"Step"}
                                iconFQN={"Consilio.UI.Icons.TinkPage.Success"}
                                svgClassName={classes.iconSuccess}
                            />
                            <Rubrik
                                rubrikClassName={classnames(
                                    classes.title,
                                    classes.text
                                )}
                                textCode={"SoP.UI.Tink.SigneringKlar.Rubrik"}
                            />
                            <Ingress
                                ingressClassName={classnames(
                                    classes.ingress,
                                    classes.text
                                )}
                                textCode={"SoP.UI.Tink.SigneringKlar.Ingress"}
                            />
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <Icon
                                iconStyleType={"Step"}
                                iconFQN={"Consilio.UI.Icons.TinkPage.Error"}
                                svgClassName={classes.iconError}
                            />
                            <Rubrik
                                rubrikClassName={classnames(
                                    classes.title,
                                    classes.text
                                )}
                                textCode={"SoP.UI.Tink.Felmeddelande.Rubrik"}
                            />
                            <Ingress
                                ingressClassName={classnames(
                                    classes.ingress,
                                    classes.text
                                )}
                                textCode={"SoP.UI.Tink.Felmeddelande.Ingress"}
                            />
                            <Ingress
                                ingressClassName={classnames(
                                    classes.ingress,
                                    classes.text
                                )}
                                textCode={
                                    "SoP.UI.Tink.Felmeddelande.Ingress.StängFönstret"
                                }
                            />
                        </React.Fragment>
                    )}
                </div>
            </Container>
        </SplashPage>
    );
};

//-------------------------------------------------------------------------------------
// Exports
//-------------------------------------------------------------------------------------

export default TinkPageComponent;
